/**
 * `/settings/consumption-tax-rates` 消費税率 (Full 限定)。
 * 設計: docs/design/v05-consumption-tax-rate-and-target-tax-basis.md §3-3 / §6-8
 * 税率は % で入力し、保存は小数 (10 → 0.10)。履歴は「適用開始日つきの行を足す」方式。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { DateLabel } from '@/components/DateLabel';

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

type RateCode = 'standard' | 'reduced';
type Rate = { rate_id: string; rate_code: RateCode; rate: number; effective_from: string };
type Draft = { rate_id: string | null; rate_code: RateCode; percent: string; effective_from: string };

const RATE_CODES: { code: RateCode; label: string }[] = [
  { code: 'standard', label: '標準税率' },
  { code: 'reduced', label: '軽減税率' },
];

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// 0.1 + 浮動小数の誤差を避けるため、表示は小数第2位までに丸める
const toPercentText = (rate: number) => String(Math.round(rate * 10000) / 100);

type RateStatus = 'current' | 'scheduled' | 'past';
const STATUS_BADGE: Record<RateStatus, { label: string; cls: string }> = {
  current: { label: '適用中', cls: 'bg-misespark-navy text-white border-misespark-navy' },
  scheduled: { label: '予定', cls: 'bg-white text-text-body border-text-sub' },
  past: { label: '過去', cls: 'bg-surface-sunken text-text-sub border-surface-border' },
};

/** 区分内の行を新しい順に並べ、今日時点で適用中の行・予定・過去を判定する。 */
const classify = (rows: Rate[], today: string): { row: Rate; status: RateStatus }[] => {
  const sorted = [...rows].sort((a, b) => b.effective_from.localeCompare(a.effective_from));
  const currentId = sorted.find((r) => r.effective_from <= today)?.rate_id;
  return sorted.map((row) => ({
    row,
    status: row.rate_id === currentId ? 'current' : row.effective_from > today ? 'scheduled' : 'past',
  }));
};

export function ConsumptionTaxRatesSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'tenant_config', 'manage');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Rate | null>(null);

  const { data, isLoading, isError } = useQuery<{ rates: Rate[] }>({
    queryKey: ['consumption-tax-rates'],
    queryFn: () => apiFetch('/settings/consumption-tax-rates'),
  });
  const rates = data?.rates ?? [];
  const today = localToday();

  const percentValue = draft ? Number(draft.percent) : NaN;
  const percentValid = !!draft && draft.percent.trim() !== '' && Number.isFinite(percentValue) && percentValue >= 0 && percentValue < 100;
  const canSave = percentValid && !!draft?.effective_from;

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const rate = Math.round(Number(d.percent) * 100) / 10000;
      return d.rate_id
        ? apiFetch(`/settings/consumption-tax-rates/${d.rate_id}`, { method: 'PUT', body: JSON.stringify({ rate, effective_from: d.effective_from }) })
        : apiFetch('/settings/consumption-tax-rates', { method: 'POST', body: JSON.stringify({ rate_code: d.rate_code, rate, effective_from: d.effective_from }) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['consumption-tax-rates'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/settings/consumption-tax-rates/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['consumption-tax-rates'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const labelOf = (code: RateCode) => RATE_CODES.find((c) => c.code === code)?.label ?? code;

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-base font-semibold text-text-body">消費税率</h2>
        <p className="text-sm text-text-sub mt-1">
          税込で入力された売上目標・日報売上を税抜に換算するときに使います。法改正のときは新しい税率を適用開始日つきで追加してください。
        </p>
      </div>

      {isLoading ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : isError ? (
        <p role="alert" className="text-sm text-danger-text">消費税率の取得に失敗しました。</p>
      ) : (
        RATE_CODES.map(({ code, label }) => {
          const items = classify(rates.filter((r) => r.rate_code === code), today);
          const current = items.find((i) => i.status === 'current');
          return (
            <section key={code} className="space-y-2" aria-labelledby={`ctr-${code}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <h3 id={`ctr-${code}`} className="text-sm font-semibold text-text-body">{label}</h3>
                  <p className="text-sm text-text-sub">
                    現在の税率: {current ? `${toPercentText(current.row.rate)}%` : '未設定'}
                  </p>
                </div>
                {canManage && (
                  <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
                    onClick={() => setDraft({ rate_id: null, rate_code: code, percent: '', effective_from: '' })}>
                    {label}を追加
                  </Button>
                )}
              </div>
              {items.length === 0 ? (
                <EmptyState message={`${label}が登録されていません`}
                  description="税率が無い日付の金額は税抜に換算できません。" />
              ) : (
                <ul className="space-y-2">
                  {items.map(({ row, status }) => (
                    <li key={row.rate_id} className="border border-surface-border rounded-lg bg-white p-3 flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-text-body">{toPercentText(row.rate)}%</span>
                        <span className="text-sm text-text-sub">
                          <DateLabel date={row.effective_from} withYear /> から
                        </span>
                        <span className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${STATUS_BADGE[status].cls}`}>
                          {STATUS_BADGE[status].label}
                        </span>
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button type="button" aria-label={`${label} ${toPercentText(row.rate)}% を編集`}
                            onClick={() => setDraft({ rate_id: row.rate_id, rate_code: row.rate_code, percent: toPercentText(row.rate), effective_from: row.effective_from })}
                            className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                          <button type="button" aria-label={`${label} ${toPercentText(row.rate)}% を削除`} onClick={() => setDeleteTarget(row)}
                            className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })
      )}

      <Modal open={!!draft} title={draft ? `${labelOf(draft.rate_code)}を${draft.rate_id ? '編集' : '追加'}` : ''} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="ctr-percent">税率（%）</label>
              <input id="ctr-percent" type="number" inputMode="decimal" min={0} max={99.99} step="0.01" value={draft.percent}
                onChange={(e) => setDraft({ ...draft, percent: e.target.value })} placeholder="例: 10"
                aria-invalid={draft.percent.trim() !== '' && !percentValid}
                className={`${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`} />
              {draft.percent.trim() !== '' && !percentValid && (
                <p className="text-xs text-danger-text mt-1">0以上100未満の数値で入力してください</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="ctr-from">適用開始日</label>
              <input id="ctr-from" type="date" value={draft.effective_from}
                onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })} className={inputCls} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="税率を削除"
        message={deleteTarget ? `${labelOf(deleteTarget.rate_code)} ${toPercentText(deleteTarget.rate)}%（${deleteTarget.effective_from} から）を削除します。この期間の金額は直前の税率で換算されるか、税率が無ければ換算できなくなります。` : ''}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.rate_id)} />
    </div>
  );
}
